import { requireOrdinaryTimeOnly, } from "@membentry/shared-types";
const normalizeOptionalClubTime = (value, fieldName) => value == null ? value : requireOrdinaryTimeOnly(value, `club ${fieldName}`);
const normalizeClubDetails = (club) => ({
    ...club,
    openingTime: normalizeOptionalClubTime(club.openingTime, "openingTime"),
    closingTime: normalizeOptionalClubTime(club.closingTime, "closingTime"),
});
const isClubDetailsPayload = (payload) => typeof payload === "object" &&
    payload !== null &&
    ("openingTime" in payload || "closingTime" in payload);
const normalizeDetailBearingClubResponse = (response) => ({
    ...response,
    payload: isClubDetailsPayload(response.payload)
        ? normalizeClubDetails(response.payload)
        : response.payload,
});
const canonicalizeClubHours = (data) => ({
    ...data,
    ...(data.openingTime !== undefined && {
        openingTime: normalizeOptionalClubTime(data.openingTime, "openingTime"),
    }),
    ...(data.closingTime !== undefined && {
        closingTime: normalizeOptionalClubTime(data.closingTime, "closingTime"),
    }),
});
export const getAllClubs = async (client, page = 0, pageSize = 10, search) => {
    const response = await client.get(`/clubs/all?page=${page}&pageSize=${pageSize}&search=${search || ""}`);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const getClubById = async (client, id) => {
    const response = await client.get(`/clubs/${id}`);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return normalizeDetailBearingClubResponse(response.data);
};
export const getMyClub = async (client, config) => {
    const response = await client.get("/clubs/my-club", config);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return normalizeDetailBearingClubResponse(response.data);
};
export const getMyClubs = async (client, params) => {
    const response = await client.get("/clubs/my-clubs", {
        params,
    });
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const joinClub = async (client, clubId) => {
    const response = await client.post(`/clubs/${clubId}/join`);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const leaveClub = async (client, clubId) => {
    const response = await client.delete(`/clubs/${clubId}/leave`);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const createClub = async (client, data) => {
    const response = await client.post("/clubs/create", canonicalizeClubHours(data));
    return normalizeDetailBearingClubResponse(response.data);
};
export const updateClub = async (client, id, data) => {
    const response = await client.put(`/clubs/update/${id}`, canonicalizeClubHours(data));
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return normalizeDetailBearingClubResponse(response.data);
};
export const updateClubLogo = async (client, id, logoImageUrl) => {
    const response = await client.put(`/clubs/${id}/logo`, { logoImageUrl });
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return normalizeDetailBearingClubResponse(response.data);
};
export const deleteClub = async (client, id) => {
    await client.delete(`/clubs/delete/${id}`);
};
export const getClubMembers = async (client, params) => {
    const { clubId, filters, ...queryParams } = params;
    const response = await client.get(`/clubs/${clubId}/members`, {
        params: {
            ...queryParams,
            ...filters,
        },
    });
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const getVisibleClubMembers = async (client, params) => {
    const { clubId, filters, ...queryParams } = params;
    const response = await client.get(`/clubs/${clubId}/members/visible`, {
        params: {
            ...queryParams,
            ...filters,
        },
    });
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const getClubEntries = async (client, clubId, params) => {
    const response = await client.get(`/clubs/${clubId}/entries`, { params });
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const getTodayClubEntries = async (client, clubId) => {
    const response = await client.get(`/clubs/${clubId}/entries/today`);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const getMyClubEntries = async (client, params) => {
    const response = await client.get("/clubs/my-club/entries", { params });
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const getMyClubTodayEntries = async (client, params) => {
    const response = await client.get("/clubs/my-club/entries/today", { params });
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const getClubDailySummary = async (client, date) => {
    const response = await client.get("/clubs/my-club/daily-summary", date ? { params: { date } } : undefined);
    if (response.data === undefined)
        throw new Error("No data received from server");
    return response.data;
};
export const getMyClubRecentOccupancy = async (client, recentMinutes = 90) => {
    const response = await client.get("/clubs/my-club/entries/today", { params: { recentMinutes } });
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
export const getMyClubMembership = async (client, clubId) => {
    const response = await client.get(`/clubs/${clubId}/my-membership`);
    if (response.data === undefined) {
        throw new Error("No data received from the server");
    }
    return response.data;
};
